import type { MjcfJoint } from '@/domain/rig'
import type { Rig } from '@/domain/rig'
import type { PlaneKey, Side, Simulation } from '@/domain/hs1'

/**
 * Die Sollbahn für MuJoCo — und die Kanäle, gegen die verglichen wird.
 *
 * DAS IST DIE STELLE, AN DER DIE GEGENPROBE ENTSTEHT, und sie ist bewusst frei
 * von MuJoCo: hier wird nur aus dem Rechenkern abgeleitet, was die Vorwärts-
 * rechnung nachfahren soll. Ohne WebAssembly, ohne Browser, prüfbar wie jede
 * andere Rechnung.
 *
 * ZWEI RICHTUNGEN, EIN MODELL. Der Kern rechnet RÜCKWÄRTS: er gibt die Bewegung
 * vor und sucht die Kräfte, die sie erzeugen. MuJoCo rechnet VORWÄRTS: es gibt
 * die Kräfte vor und sucht die Bewegung, die daraus folgt. Stimmen beide
 * überein, ist das eine Aussage über das Modell. Gehen sie auseinander, steht
 * an genau der Stelle eine Frage — und die Stelle ist die eigentliche Auskunft.
 *
 * WAS DABEI HERAUSKOMMEN KANN UND WAS NICHT. Eine Abweichung heisst nicht, dass
 * der Kern falsch rechnet. Sie kann drei Ursachen haben, und alle drei sind
 * interessant:
 *
 *   – Das Moment reicht nicht. Der Regler läuft in `ctrlrange`, die Achse
 *     bleibt zurück, der Folgefehler wächst genau dort im Zyklus, wo die
 *     Auslegung zu schwach ist. DAS ist die Auskunft, für die das MJCF gebaut
 *     wurde.
 *   – Der Regler taugt nicht. Er ist eine Annahme dieser Datei und keine
 *     Konstruktionsgrösse — siehe `PdGains`.
 *   – Die Vorzeichen passen nicht zusammen. Siehe `PLANE_OF_AXIS`.
 */

/** Welche Ebene des Gangmodells zu welcher Drehachse des Achsbaums gehört. */
const PLANE_OF_AXIS: Record<string, PlaneKey> = {
  /* Nicken um die Querachse — Beugen und Strecken. */
  y: 'sag',
  /* Rollen um die Längsachse — Ab- und Anspreizen. */
  x: 'fro',
  /* Gieren um die Hochachse. */
  z: 'tra',
}

/**
 * Die Verstärkung des Nachführreglers.
 *
 * SIE IST EINE ANNAHME UND KEINE AUSLEGUNG — dieselbe Art Zahl wie der
 * Reibbeiwert im MJCF, und sie steht aus demselben Grund benannt hier statt
 * verstreut im Code.
 *
 * `fullAt` ist der Winkelfehler, bei dem der Regler das volle Achsmoment
 * fordert. Daraus fällt die Steifigkeit je Achse: eine Achse mit 300 Nm
 * bekommt eine steifere Nachführung als eine mit 40 Nm, und beide erreichen
 * ihre Grenze beim gleichen Fehler. Das ist der Punkt: der Regler soll KEINE
 * Achse bevorzugen, damit ein Folgefehler etwas über die AUSLEGUNG aussagt und
 * nicht über die Reglereinstellung.
 *
 * `damping` ist die Vorhaltezeit in Sekunden. Ohne sie schwingt jede Achse um
 * ihren Sollwert, und der Folgefehler misst das Schwingen statt der Auslegung.
 */
export interface PdGains {
  /** Winkelfehler, bei dem das volle Achsmoment gefordert wird, rad. */
  fullAt: number
  /** Vorhaltezeit, s. */
  damping: number
}

export const DEFAULT_GAINS: PdGains = { fullAt: 0.15, damping: 0.04 }

/** Eine Achse mit ihrer Sollbahn und ihren Reglerzahlen. */
export interface TrackedJoint {
  joint: MjcfJoint
  /** Sollwinkel je Stützstelle, RAD. */
  target: Float32Array
  /** Steifigkeit, Nm/rad. */
  kp: number
  /** Dämpfung, Nms/rad. */
  kd: number
  /**
   * Gibt es für diese Achse eine aufgezeichnete Kurve?
   *
   * `false` heisst: der Referenzgang kennt dieses Gelenk nicht. Die Achse wird
   * dann auf null gehalten — das ist eine Vorgabe und keine Bewegung, und ein
   * Folgefehler dort sagt nichts über die Auslegung.
   */
  known: boolean
}

export interface TrackPlan {
  joints: TrackedJoint[]
  /** Stützstellen je Zyklus. */
  samples: number
  /** Dauer des Doppelschritts, s. */
  cycleTime: number
  /** Wie viele Achsen ohne aufgezeichnete Kurve mitlaufen. */
  unknown: number
  /** Die Kanäle des Rechenkerns, gegen die verglichen wird. */
  reference: Reference
}

/**
 * Was der Kern über den Zyklus sagt — die eine Hälfte des Vergleichs.
 *
 * Alle Reihen haben `samples` Werte und beginnen beim Aufsetzen des rechten
 * Fusses, wie überall im Kern.
 */
export interface Reference {
  /** Senkrechte Bodenreaktionskraft, beide Füsse zusammen, N. */
  grf: Float32Array
  /** Höhe des Körperschwerpunkts über dem Boden, m. */
  comHeight: Float32Array
  /** Vorwärtsweg des Schwerpunkts, m. */
  comForward: Float32Array
}

const DEG = Math.PI / 180

/**
 * Baut die Sollbahn aus einer gerechneten Simulation.
 *
 * Rein: gleiche Simulation, gleicher Plan. Der Plan hängt NICHT an MuJoCo und
 * weiss nichts von WebAssembly — er ist die Übersetzung des Gangs in die
 * Sprache der Vorwärtsrechnung.
 */
export function trackPlan(
  simulation: Simulation,
  rig: Rig,
  joints: readonly MjcfJoint[],
  gains: PdGains = DEFAULT_GAINS,
): TrackPlan {
  const samples = simulation.gait.samples
  const angles = simulation.gait.angles

  let unknown = 0
  const tracked: TrackedJoint[] = joints.map((joint) => {
    const node = rig.byId[joint.node]
    const role = node?.role ?? null
    const plane = PLANE_OF_AXIS[joint.axis]
    const side: Side = joint.side === 'L' ? 'L' : 'R'
    const reihe = role && plane ? angles[role]?.[plane]?.[side] : undefined

    const target = new Float32Array(samples)
    if (reihe) {
      /* Der Gang steht in GRAD, MuJoCo rechnet in Bogenmass — dieselbe
         Umrechnung wie im Keyframe und aus demselben Grund. */
      for (let i = 0; i < samples; i++) target[i] = (reihe[i] ?? 0) * DEG
    } else {
      unknown++
    }

    /* Volles Moment beim festgelegten Fehler — siehe `PdGains`. */
    const kp = joint.moment / gains.fullAt
    return { joint, target, kp, kd: kp * gains.damping, known: reihe !== undefined }
  })

  return {
    joints: tracked,
    samples,
    cycleTime: simulation.gait.cycleTime,
    unknown,
    reference: reference(simulation),
  }
}

/**
 * Die Kanäle des Kerns, auf `samples` Stützstellen.
 *
 * DIE BODENKRAFT WIRD ADDIERT UND NICHT GEMITTELT: MuJoCo misst, was der Boden
 * insgesamt trägt, und der Kern führt sie je Fuss. Wer nur einen Fuss nimmt,
 * vergleicht im Doppelstand die Hälfte mit dem Ganzen.
 */
function reference(simulation: Simulation): Reference {
  const n = simulation.gait.samples
  const grf = new Float32Array(n)
  const comHeight = new Float32Array(n)
  const comForward = new Float32Array(n)

  const { com } = simulation.dynamics
  for (let i = 0; i < n; i++) {
    const r = simulation.dynamics.grf.R[i]
    const l = simulation.dynamics.grf.L[i]
    grf[i] = (r?.y ?? 0) + (l?.y ?? 0)
    comHeight[i] = com[i]?.y ?? 0
    comForward[i] = com[i]?.x ?? 0
  }
  return { grf, comHeight, comForward }
}

/**
 * Der Sollwinkel zu einer Zeit, zyklisch interpoliert.
 *
 * DIE ZEIT LÄUFT WEITER, DER ZYKLUS NICHT. MuJoCo rechnet in Millisekunden-
 * schritten und der Gang hat gut hundert Stützstellen je Doppelschritt; ohne
 * Interpolation bekäme jede Achse eine Treppe, und die Treppe erzeugte
 * Momentspitzen, die niemand ausgelegt hat.
 */
export function targetAt(joint: TrackedJoint, t: number, plan: TrackPlan): number {
  const n = plan.samples
  if (n === 0) return 0
  const phase = ((t / plan.cycleTime) % 1 + 1) % 1
  const x = phase * n
  const i = Math.floor(x)
  const f = x - i
  const a = joint.target[i % n] ?? 0
  const b = joint.target[(i + 1) % n] ?? 0
  return a + (b - a) * f
}

/**
 * Das Stellmoment einer Achse — PD, an der Auslegungsgrenze abgeschnitten.
 *
 * DAS ABSCHNEIDEN IST DER ZWECK DER ÜBUNG und keine Notmassnahme. `ctrlrange`
 * im MJCF ist genau das Moment, das die Achse laut Auslegung hat. Solange es
 * reicht, folgt sie ihrer Kurve; sobald es nicht reicht, bleibt sie zurück —
 * und der Folgefehler zeigt, wo die Auslegung zu schwach ist.
 */
export function torque(joint: TrackedJoint, soll: number, ist: number, rate: number): number {
  const roh = joint.kp * (soll - ist) - joint.kd * rate
  const grenze = joint.joint.moment
  return Math.min(grenze, Math.max(-grenze, roh))
}
